import Lottie from "lottie-react";
import { Calculator } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NodeProps } from "reactflow";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import {
  useCreateDrawing,
  useGetDrawingToolData,
  usePutDrawingToolDataToQueue,
  useSendErrorDetails,
  useUpdateDrawing
} from "../../api/drawingTool";
import { useCreateProcessGate, useUpdateProcessGate } from "../../api/processGate/processGate";
import { useGetDefaultVersion, useGetVersionById } from "../../api/versioningService";
import calculatorLoading from "../../assets/animations/CalculatorLoading.json";
import { PopupButton } from "../../components";
import ConfirmModal from "../../components/confirmViewModal/ConfirmModal";
import DrawingTool from "../../components/drawing-tool/DrawingTool";
import {
  MappingHeaderCard,
  useMappingHeaderInfo
} from "../../components/drawing-tool/mapping-header";
import {
  EditMappingDialog,
  EditMappingResult,
  EditMode,
  useNextVersionInfo
} from "../../components/drawing-tool/mapping-version";
import { sortNodes } from "../../components/drawing-tool/utils";
import { Button as TailwindButton } from "../../components/ui/common/button";
import {
  DISABLE_REVIEW_BUTTON,
  ENABLE_MAPPING_INGREDIENT_UI_REDESIGN
} from "../../config/constants";
import { MappingSourceType, MappingType } from "../../config/enums/MappingSourceType.enum";
import { ROLES } from "../../config/permission-maps";
import { errorView, successMessage } from "../../helpers/ErrorHandler";
import useUploadMappingImage from "../../hooks/useUploadMappingImage";
import { getUserRolesInLocal } from "../../utils/cacheStorage";
import { getStringInsideBrackets } from "../../utils/utils";
import CopyMappingModal from "./CopyMappingModal";
import { DeleteMappingDialog } from "./deleteMapping";
import { MULTIPLE_ENDPOINTS_ERROR } from "./endpointValidation/constants/endpointValidation.constants";
import { validateSingleEndpoint } from "./endpointValidation/helpers/endpointValidation.helpers";
import { useDrawingInitialData } from "./hooks/useDrawingInitialData";
import { useMissingMappingData } from "./hooks/useMissingMappingData";
import { useRTCProvider } from "./hooks/useRTCProvider";
import ImportMappingModal from "./ImportMappingModal";
import DrawingErrorsModal from "./MappingErrorsModal";
import "./MappingTool.scss";
import ProcessGateFormModal from "./UpdateNodes/ProcessGate/components/ProcessGateFormModal";
import ProcessGateFormModalLegacy from "./UpdateNodes/ProcessGate/components/ProcessGateFormModalLegacy";
import UpdateNodeModal from "./UpdateNodes/UpdateNodeModal";
import { canCreateNewVersion, isHistoricalVersionView } from "./versioningHelpers";

// Redesigned Process Gate form is gated by the same flag as the Ingredient redesign.
const ProcessGateForm = ENABLE_MAPPING_INGREDIENT_UI_REDESIGN
  ? ProcessGateFormModal
  : ProcessGateFormModalLegacy;

const MappingTool = () => {
  // Drawing States
  const [searchParams, setSearchParams] = useSearchParams();
  const paramId = searchParams.get("sourceId");

  const { mappingType: paramMappingType } = useParams<string>();
  const mappingType = paramMappingType as keyof typeof MappingSourceType;
  const mappingSourceType = MappingSourceType[mappingType];

  const [isVisibleCopyModal, setIsVisibleCopyModal] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  const [isVisibleImportModal, setIsVisibleImportModal] = useState(false);
  const [isVisibleChangeRequestModal, setIsVisibleChangeRequestModal] = useState<boolean>(false);
  const [processGateNode, setProcessGateNode] = useState<NodeProps | null>();
  const [isProcessGateCreated, setIsProcessGateCreated] = useState<boolean>(false);
  const [productData, saveProductData] = useState<any>({});

  // Dropdown States
  const [sourceId, setSourceId] = useState<string>("");
  const [calculateModalOpen, setCalculateModalOpen] = useState<boolean>(false);
  const [putDataToQueueErrors, setPutDataToQueueErrors] = useState<string[]>([]);
  const [calculateErrorMessage, setCalculateErrorMessage] = useState<Boolean>(false);
  const [enableLoading, setEnableLoading] = useState<Boolean>(false);
  const [ownerId, setOwnerId] = useState<any>("");
  const [selectProductDetails, setSelectProductDetails] = useState<any>(null);

  const navigate = useNavigate();

  useRTCProvider(paramId, () => {
    const sourceId = searchParams.get("sourceId");
    setSourceId(sourceId || "");
  });

  const { mutate: createProcessGateMutate } = useCreateProcessGate();

  const {
    ownerId: rawOwnerId,
    selectionData,
    salesUnitsById
  } = useDrawingInitialData({
    // The mapping header uses a plain Select (per the approved design), so there is no
    // search box feeding this any more -- the full sales-unit list is always fetched.
    searchKey: "",
    sourceId,
    saveProductData,
    mappingSourceType: mappingSourceType
  });

  useEffect(() => {
    if (rawOwnerId !== undefined && rawOwnerId !== ownerId) {
      setOwnerId(rawOwnerId);
    }
  }, [rawOwnerId, ownerId]);

  const {
    data: drawingToolData,
    isLoading: isDrawingToolLoading,
    refetch: reFetchDrawingTool
  } = useGetDrawingToolData(mappingSourceType, sourceId, ownerId);

  const versionIdParam = searchParams.get("versionId");

  const { data: defaultVersion } = useGetDefaultVersion(
    drawingToolData?._id || "",
    !!drawingToolData?._id
  );

  const { data: viewedVersion } = useGetVersionById(versionIdParam || "", !!versionIdParam);

  // Light summaries-backed endpoint (already fetched for the Edit Mapping dialog), so the Remove
  // dialog can tell a single-version mapping from a multi-version one without pulling snapshots.
  const { versionCount } = useNextVersionInfo(drawingToolData?._id);

  const isViewingHistoricalVersion = isHistoricalVersionView(versionIdParam, viewedVersion);
  const canCreateVersion = canCreateNewVersion(defaultVersion);

  const { versionLabel, climateImpact, productSummaryPath } = useMappingHeaderInfo({
    defaultVersion,
    viewedVersion,
    isViewingHistoricalVersion,
    salesUnit: selectProductDetails || salesUnitsById[sourceId]
  });

  const exitHistoricalView = () => {
    searchParams.delete("versionId");
    setSearchParams(searchParams);
  };

  const handleEditMappingSuccess = (result: EditMappingResult) => {
    if (result.mode === EditMode.NewVersion) {
      exitHistoricalView();
    }
    reFetchDrawingTool();
  };

  const [missingNodeIds, setMissingNodeIds, checkForMissingMappingData] = useMissingMappingData();

  const [isLoadingImageUpload, uploadImage] = useUploadMappingImage(
    drawingToolData?.chartNodes || [],
    mappingSourceType
  );

  const { mutate: updateProcessGateAction } = useUpdateProcessGate();

  const { mutate: createDrawing, isLoading: isLoadingCreateDrawing } =
    useCreateDrawing(mappingSourceType);

  const { mutate: updateDrawing, isLoading: isLoadingUpdateDrawing } =
    useUpdateDrawing(mappingSourceType);

  const { isLoading: isLoadingPutDataToQueue, mutate: putDrawingToolDataToQueueAsync } =
    usePutDrawingToolDataToQueue(mappingSourceType);

  const { mutate: sendErrorDetails } = useSendErrorDetails(mappingSourceType);

  const saveDrawing = useCallback(
    (chartEdges: any, chartNodes: any, status: boolean = false, callBack?: () => void) => {
      setEnableLoading(status);
      // SHARED3 Fix: Use fresh drawingToolData from dependency, not stale closure
      if (drawingToolData?._id) {
        updateDrawing(
          {
            ...drawingToolData,
            chartEdges: chartEdges,
            chartNodes: chartNodes
          },
          {
            onSuccess: () => {
              reFetchDrawingTool();
              checkForMissingMappingData(chartNodes, () => {
                setMissingNodeIds([]);
              });
              setEnableLoading(false);
              // CRITICAL-4 Fix: Only call callBack if it's defined
              if (callBack) {
                callBack();
              }
            },
            // SHARED2 Fix: Add onError handler
            onError: (error: any) => {
              setEnableLoading(false);
              errorView(error?.msg || "Failed to save drawing");
            }
          }
        );
        return;
      }
      const newDrawing = {
        sourceId,
        ownerId,
        chartEdges: chartEdges,
        chartNodes: chartNodes
      };
      createDrawing(newDrawing, {
        onSuccess: () => {
          reFetchDrawingTool();
          checkForMissingMappingData(chartNodes, () => {
            setMissingNodeIds([]);
          });
          // CRITICAL-4 Fix: Only call callBack if it's defined
          if (callBack) {
            callBack();
          }
        },
        // SHARED2 Fix: Add onError handler
        onError: (error: any) => {
          setEnableLoading(false);
          errorView(error?.msg || "Failed to create drawing");
        }
      });
    },
    [
      drawingToolData,
      sourceId,
      ownerId,
      updateDrawing,
      createDrawing,
      reFetchDrawingTool,
      checkForMissingMappingData
    ]
  );

  const saveMappingBeforeCalculate = useCallback(
    (chartEdges: any, chartNodes: any) => {
      // The calculation engine takes one endpoint; stop before anything is saved or queued.
      if (validateSingleEndpoint(chartNodes, chartEdges).blocked) {
        setPutDataToQueueErrors([MULTIPLE_ENDPOINTS_ERROR]);
        return;
      }
      checkForMissingMappingData(chartNodes, errorMessages => {
        setCalculateModalOpen(true);
        if (errorMessages.length > 0) {
          setCalculateErrorMessage(true);
          return;
        }
        setCalculateErrorMessage(false);
        setPutDataToQueueErrors([]);
        saveDrawing(chartEdges, chartNodes, false);
      });
    },
    [saveDrawing, checkForMissingMappingData]
  );

  const navigateReview = () => {
    return navigate(
      `/mapping/review/${mappingSourceType}/${
        drawingToolData?._id
      }/${ownerId}/${sourceId}?isAverageProduct=${
        selectProductDetails?.isAverageProduct || false
      }&isEditable=${!drawingToolData?.lockStatus}`
    );
  };

  // Email handler
  const handleClickEmail = async () => {
    const companyName = localStorage.getItem("companyName");
    const mappingUrl = window.location.href;
    setCalculateModalOpen(false);

    if (!ownerId || !sourceId || !companyName || !mappingUrl) {
      return;
    }

    sendErrorDetails(
      {
        ownerId,
        sourceId,
        companyName,
        mappingUrl
      },
      {
        onSuccess: () => {
          setCalculateErrorMessage(false);
        },
        onError: error => {
          errorView(`Error sending details: ${error}`);
        }
      }
    );
  };

  const uploadMappingImage = () => {
    if (!sourceId) return;

    uploadImage(sourceId, ownerId, () => {
      if (mappingSourceType === MappingSourceType.ingredient) {
        navigate("/ingredient");
      } else {
        navigate(`/product-summary?salesUnitId=${sourceId}&productId=${ownerId}`);
      }
    });
  };

  const calculateHandleApprove = () => {
    putDrawingToolDataToQueueAsync(drawingToolData, {
      onError: (error: any) => {
        const checkValidationError = error?.response?.data?.data;
        if (
          checkValidationError &&
          Array.isArray(checkValidationError) &&
          checkValidationError.length > 0
        ) {
          setPutDataToQueueErrors(checkValidationError);
          setCalculateModalOpen(false);
          return;
        }
        setCalculateErrorMessage(true);
        setCalculateModalOpen(true);
      },
      onSuccess: () => {
        setCalculateModalOpen(false);
        setCalculateErrorMessage(false);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            uploadMappingImage();
          });
        });
      }
    });
  };

  const toggleProcessGateModel = (node: NodeProps | null, isCreated: boolean) => {
    setIsProcessGateCreated(isCreated);
    setProcessGateNode(node);
  };

  //update process gate data
  const updateProcessGate = (processGateName: string, newNode: NodeProps) => {
    const facilityId = newNode.data.facilityId;
    let processGate = {
      processGateId: processGateNode?.data.reference.processGateId,
      processGateName,
      facilityId,
      ownerId,
      sourceId,
      userCompletedStage: "RECIPE"
    };
    updateProcessGateAction(processGate, {
      onSuccess(data) {
        updateProcessGateData(newNode, {
          processGateId: data._id,
          ownerId: data?.ownerId,
          sourceId: data?.sourceId
        });
        toggleProcessGateModel(null, false);
      }
    });
  };

  const updateProcessGateData = (nodeItem: any, processData: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        reference: processData
      }
    };
    const chartNodes = drawingToolData?.chartNodes || [];
    const chartEdges = drawingToolData?.chartEdges || [];
    const updatedChartNodes = [...chartNodes.filter(n => n.id !== nodeItem.id), updatedNode].sort(
      sortNodes
    );
    saveDrawing(chartEdges, updatedChartNodes, false, () => {
      successMessage("Update process gate successfully");
    });
  };

  //create process gate data
  const createProcessGate = (processGateName: string, newNode: NodeProps) => {
    const facilityId = newNode.data.facilityId;
    let processGate = {
      processGateName,
      facilityId,
      ownerId,
      sourceId,
      userCompletedStage: "RECIPE",
      processing: [],
      internalTransportation: [],
      internalStorage: [],
      packaging: []
    };
    createProcessGateMutate(processGate, {
      onSuccess(data) {
        saveProcessGateData(newNode, {
          processGateId: data._id,
          ownerId: data?.ownerId,
          sourceId: data?.sourceId
        });
        toggleProcessGateModel(null, false);
      }
    });
  };

  //save transport node data
  const saveProcessGateData = (nodeItem: any, processData: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        reference: processData
      }
    };
    const chartNodes = drawingToolData?.chartNodes || [];
    const chartEdges = drawingToolData?.chartEdges || [];
    const updatedChartNodes = [...chartNodes, updatedNode].sort(sortNodes);
    saveDrawing(chartEdges, updatedChartNodes, false, () => {
      successMessage("Update process gate successfully");
    });
  };

  // Data for dropdown
  const updateSourceId = (data: any) => {
    const id = data.value as string;
    const getSelectedProduct = salesUnitsById[id];
    setSelectProductDetails(getSelectedProduct);
    setSourceId(id);
    searchParams.set("sourceId", id);
    setSearchParams(searchParams);
  };

  const cancel = () => {
    setCalculateModalOpen(false);
    setCalculateErrorMessage(false);
  };

  const checkDrawingButtonStatus = useMemo(() => getUserRolesInLocal(), [getUserRolesInLocal]);

  const checkUserIsSupplier = checkDrawingButtonStatus?.[0] === ROLES.supplier;

  const calculationTile = checkUserIsSupplier
    ? "Are you sure you have entered all the data for complete calculation?"
    : "Are you sure you have entered all the data for calculation?";

  if (!sourceId && mappingType === MappingSourceType.product) {
    return (
      <>
        {/* The card's z-[99] must be contained in its own stacking context, or Radix portals
            (company/version selects, z-50, mounted at document.body) render underneath it --
            in the canvas branch that containment comes for free from Semantic's
            Sidebar.Pushable segment (`.pushable:not(body)` sets a transform). */}
        <div className="tw-relative tw-isolate">
          <MappingHeaderCard
            productName={selectProductDetails?.productName ?? salesUnitsById[sourceId]?.productName}
            salesUnitName={
              selectProductDetails?.salesUnitName ?? salesUnitsById[sourceId]?.salesUnitName
            }
            options={selectionData}
            selectedValue={sourceId}
            onSelect={value => updateSourceId({ value })}
            versionLabel={versionLabel}
            climateImpact={climateImpact}
            productSummaryPath={productSummaryPath}
          />
        </div>
        <div className="product-empty-description">
          <h3>
            Please select the product or create the product if product is not available from the
            dropdown.
          </h3>
        </div>
      </>
    );
  }

  // Loading state check
  if (
    isDrawingToolLoading ||
    isLoadingCreateDrawing ||
    (isLoadingUpdateDrawing && enableLoading) ||
    isLoadingImageUpload
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  if (isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <div className="loading-calculation">
          <Lottie animationData={calculatorLoading} loop={true} />
        </div>
      </Dimmer>
    );
  }
  const isIngredientMap = mappingType === MappingSourceType.ingredient;
  return (
    <div className="mapping-tool-content">
      <DrawingTool
        sourceId={sourceId}
        ownerId={ownerId}
        lockStatus={isViewingHistoricalVersion ? true : drawingToolData?.lockStatus}
        chartNodesData={
          (isViewingHistoricalVersion ? viewedVersion?.chartNodes : drawingToolData?.chartNodes) ||
          []
        }
        chartEdgesData={
          (isViewingHistoricalVersion ? viewedVersion?.chartEdges : drawingToolData?.chartEdges) ||
          []
        }
        saveDrawing={saveDrawing}
        actionBar={
          mappingType === MappingSourceType.product ? (
            <MappingHeaderCard
              productName={
                selectProductDetails?.productName ?? salesUnitsById[sourceId]?.productName
              }
              salesUnitName={
                selectProductDetails?.salesUnitName ?? salesUnitsById[sourceId]?.salesUnitName
              }
              options={selectionData}
              selectedValue={sourceId}
              onSelect={value => updateSourceId({ value })}
              versionLabel={versionLabel}
              climateImpact={climateImpact}
              productSummaryPath={productSummaryPath}
              checkUseeRoleUser={checkUserIsSupplier}
            />
          ) : null
        }
        updateNodeModal={() => <UpdateNodeModal sourceId={sourceId} ownerId={ownerId} />}
        actionButtons={(chartNodes: any, chartEdges: any) => {
          if (isViewingHistoricalVersion) {
            return (
              <Grid className="mapping-action-buttons">
                <Grid.Row>
                  <PopupButton
                    tooltip="Return to the current mapping."
                    buttonClass="drawing-button"
                    buttonName="Exit Historical View"
                    onClick={() => {
                      exitHistoricalView();
                    }}
                  />
                </Grid.Row>
              </Grid>
            );
          }
          return (
            <Grid className="mapping-action-buttons">
              <Grid.Row>
                {!drawingToolData?._id && !isIngredientMap && (
                  <PopupButton
                    tooltip="Import Existing Mapping."
                    buttonClass="drawing-button"
                    buttonName="Import"
                    onClick={() => {
                      setIsVisibleImportModal(true);
                    }}
                  />
                )}
                {/* There is nothing to remove until a mapping exists -- the button used to show
                    on the empty canvas too. */}
                {drawingToolData?._id ? (
                  <PopupButton
                    tooltip="Remove Mapping."
                    buttonClass="drawing-button"
                    buttonName="Delete"
                    onClick={() => {
                      setIsVisibleDeleteModal(true);
                    }}
                  />
                ) : null}
                {drawingToolData?._id && mappingSourceType !== MappingSourceType.ingredient ? (
                  <PopupButton
                    tooltip="Copy Mapping."
                    buttonClass="drawing-button"
                    buttonName="Duplicate"
                    onClick={() => {
                      setIsVisibleCopyModal(true);
                    }}
                  />
                ) : null}
                {drawingToolData?.lockStatus ? (
                  <>
                    <PopupButton
                      tooltip="Edit Mapping Data."
                      buttonClass="drawing-button"
                      buttonName="Edit Mapping"
                      onClick={() => {
                        setIsVisibleChangeRequestModal(true);
                      }}
                    />
                    {!DISABLE_REVIEW_BUTTON ? (
                      <PopupButton
                        tooltip="Review Mapping Data."
                        buttonClass="drawing-button"
                        buttonName={"Review"}
                        onClick={() => {
                          navigateReview();
                        }}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <PopupButton
                      tooltip="Save Mapping."
                      buttonClass="drawing-button"
                      buttonName="Save"
                      onClick={() => {
                        saveDrawing(chartEdges, chartNodes, true, () => {
                          successMessage("Save successfully");
                        });
                      }}
                    />
                    <PopupButton
                      tooltip="Calculate Mapping Data."
                      buttonClass="drawing-button"
                      buttonName={checkUserIsSupplier ? "Complete" : "Calculate"}
                      onClick={() => {
                        saveMappingBeforeCalculate(chartEdges, chartNodes);
                      }}
                    />
                    {!DISABLE_REVIEW_BUTTON ? (
                      <PopupButton
                        tooltip="Review Mapping Data."
                        buttonClass="drawing-button"
                        buttonName={"Review"}
                        onClick={() => {
                          navigateReview();
                        }}
                      />
                    ) : null}
                  </>
                )}
              </Grid.Row>
            </Grid>
          );
        }}
        contextProps={{
          productData,
          mappingSourceType,
          missingNodeIds,
          toggleProcessGateModel,
          saveProductData,
          setMissingNodeIds
        }}
      />
      <ConfirmModal
        viewModal={calculateModalOpen}
        closeModal={() => {
          setCalculateModalOpen(false);
          setCalculateErrorMessage(false);
        }}
        cancel={() => cancel()}
        approve={calculateHandleApprove}
        title={"Calculation Mapping Data"}
        confirmText={checkUserIsSupplier ? "Complete" : "Calculate"}
        variant="default"
        icon={<Calculator className="tw-h-5 tw-w-5" />}
        subTitle={
          calculateErrorMessage
            ? "Are you sure you have entered all the required data for calculations? Please check the data again or contact Nature Preserve."
            : calculationTile
        }
        otherConditionCheck={calculateErrorMessage}
        otherConditionBottomButton={
          <div className="tw-flex tw-w-full tw-flex-wrap tw-justify-end tw-gap-2">
            <TailwindButton type="button" variant="outline" onClick={cancel}>
              Check Data
            </TailwindButton>
            <TailwindButton type="button" onClick={handleClickEmail}>
              Send Details About the Issue
            </TailwindButton>
          </div>
        }
      />
      {isVisibleCopyModal ? (
        <CopyMappingModal
          drawingId={drawingToolData?._id}
          closeModal={() => {
            setIsVisibleCopyModal(false);
          }}
          onSuccessCopy={(sourceId: any) => {
            setSourceId(sourceId);
          }}
        />
      ) : null}
      <DrawingErrorsModal
        errors={putDataToQueueErrors}
        setPutDataToQueueErrors={setPutDataToQueueErrors}
      />
      {processGateNode ? (
        <ProcessGateForm
          setProcessGateNode={toggleProcessGateModel}
          newNode={processGateNode}
          edit={isProcessGateCreated}
          processGateName={getStringInsideBrackets(processGateNode?.data?.label)}
          updateProcessGate={updateProcessGate}
          createProcessGate={createProcessGate}
          chartNodes={drawingToolData?.chartNodes || []}
        />
      ) : null}
      <DeleteMappingDialog
        open={isVisibleDeleteModal}
        onOpenChange={setIsVisibleDeleteModal}
        drawingId={drawingToolData?._id}
        mappingSourceType={mappingSourceType}
        currentVersionId={defaultVersion?._id}
        currentVersionNumber={defaultVersion?.versionNumber}
        versionCount={versionCount}
        hideAllVersions={checkUserIsSupplier}
        onDeleted={() => {
          // Removing all versions clears the canvas; removing the active version re-renders the
          // successor the backend just activated. Both are the same refetch.
          reFetchDrawingTool();
        }}
      />

      {isVisibleImportModal ? (
        <ImportMappingModal
          drawingId={drawingToolData?._id}
          ownerId={ownerId}
          sourceId={sourceId}
          mappingSourceType={mappingSourceType}
          closeModal={() => {
            setIsVisibleImportModal(false);
          }}
          onSuccessCopy={(sourceId: any) => {
            setSourceId(sourceId);
            reFetchDrawingTool();
          }}
        />
      ) : null}

      <EditMappingDialog
        open={isVisibleChangeRequestModal}
        onOpenChange={setIsVisibleChangeRequestModal}
        drawingToolId={drawingToolData?._id}
        ownerId={ownerId}
        sourceId={sourceId}
        mappingType={MappingType[mappingSourceType]}
        canCreateNewVersion={canCreateVersion}
        restrictToUpdateOnly={checkUserIsSupplier}
        onSuccess={handleEditMappingSuccess}
      />
    </div>
  );
};

export default MappingTool;
